// Combo UI Component
// Visual display for combo system with skill chain hints and combo counter

import type { ComboChain, ComboState } from '@systems/ComboSystem';

export class ComboUI {
  private container: HTMLDivElement;
  private comboCounter: HTMLDivElement;
  private comboName: HTMLDivElement;
  private progressBar: HTMLDivElement;
  private nextSkillHint: HTMLDivElement;
  private isVisible: boolean = false;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'combo-ui';
    this.container.innerHTML = `
      <div class="combo-counter">0</div>
      <div class="combo-name"></div>
      <div class="combo-progress">
        <div class="combo-progress-fill"></div>
      </div>
      <div class="combo-next-skill">Next: <span class="skill-name"></span></div>
    `;
    this.container.style.cssText = `
      position: absolute;
      top: 30%;
      left: 50%;
      transform: translateX(-50%);
      text-align: center;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.2s ease-out;
      z-index: 1000;
    `;

    this.comboCounter = this.container.querySelector('.combo-counter') as HTMLDivElement;
    this.comboName = this.container.querySelector('.combo-name') as HTMLDivElement;
    this.progressBar = this.container.querySelector('.combo-progress-fill') as HTMLDivElement;
    this.nextSkillHint = this.container.querySelector('.combo-next-skill') as HTMLDivElement;

    this.applyStyles();
    document.body.appendChild(this.container);
  }

  private applyStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      #combo-ui .combo-counter {
        font-size: 72px;
        font-weight: bold;
        color: #ffcc00;
        text-shadow:
          0 0 20px #ff8800,
          0 0 40px #ff6600,
          2px 2px 4px #000;
        font-family: 'Impact', sans-serif;
        animation: pulse 0.3s ease-out;
      }

      @keyframes pulse {
        0% { transform: scale(1.3); }
        100% { transform: scale(1); }
      }

      #combo-ui .combo-name {
        font-size: 24px;
        color: #ffffff;
        text-shadow: 2px 2px 4px #000;
        margin-top: 5px;
        font-family: 'Segoe UI', sans-serif;
      }

      #combo-ui .combo-progress {
        width: 200px;
        height: 8px;
        background: rgba(0, 0, 0, 0.6);
        border: 1px solid #666;
        border-radius: 4px;
        margin: 10px auto;
        overflow: hidden;
      }

      #combo-ui .combo-progress-fill {
        height: 100%;
        background: linear-gradient(to right, #ff8800, #ffcc00);
        transition: width 0.1s linear;
        box-shadow: 0 0 10px #ff8800;
      }

      #combo-ui .combo-next-skill {
        font-size: 16px;
        color: #aaaaaa;
        margin-top: 5px;
      }

      #combo-ui .combo-next-skill .skill-name {
        color: #ffcc00;
        font-weight: bold;
      }

      #combo-ui.finishing {
        animation: finisher 0.5s ease-out;
      }

      @keyframes finisher {
        0% { transform: translateX(-50%) scale(1); }
        50% { transform: translateX(-50%) scale(1.2); }
        100% { transform: translateX(-50%) scale(1); }
      }
    `;
    document.head.appendChild(style);
  }

  public showComboStart(comboName: string, totalSteps: number): void {
    this.show();
    this.comboCounter.textContent = '1';
    this.comboName.textContent = comboName;
    this.progressBar.style.width = `${(1 / totalSteps) * 100}%`;
    this.container.classList.remove('finishing');
  }

  public updateComboProgress(
    step: number,
    totalSteps: number,
    bonusDamage: number,
    nextSkillName?: string,
    timeRemaining?: number,
    totalTime?: number
  ): void {
    this.show();

    // Animate counter
    this.comboCounter.textContent = String(step);
    this.comboCounter.style.animation = 'none';
    this.comboCounter.offsetHeight; // Force reflow
    this.comboCounter.style.animation = 'pulse 0.3s ease-out';

    // Update progress bar
    this.progressBar.style.width = `${(step / totalSteps) * 100}%`;

    // Update damage multiplier display
    this.comboName.textContent = `${Math.round(bonusDamage * 100)}% Damage!`;

    // Show next skill hint
    if (nextSkillName) {
      this.nextSkillHint.style.display = 'block';
      const skillNameEl = this.nextSkillHint.querySelector('.skill-name') as HTMLSpanElement;
      skillNameEl.textContent = nextSkillName;
    } else {
      this.nextSkillHint.style.display = 'none';
    }

    // Update time bar if provided
    if (timeRemaining !== undefined && totalTime !== undefined) {
      const timePercent = (timeRemaining / totalTime) * 100;
      this.progressBar.style.width = `${timePercent}%`;
    }
  }

  public showComboComplete(comboName: string, totalBonusDamage: number): void {
    this.container.classList.add('finishing');
    this.comboCounter.textContent = '★';
    this.comboName.textContent = `${comboName} - ${Math.round(totalBonusDamage * 100)}%!`;
    this.progressBar.style.width = '100%';
    this.nextSkillHint.style.display = 'none';

    // Hide after delay
    this.scheduleHide(2000);
  }

  public showComboBroken(): void {
    this.comboCounter.textContent = '✗';
    this.comboCounter.style.color = '#ff4444';
    this.comboName.textContent = 'Combo Broken!';
    this.progressBar.style.width = '0%';
    this.nextSkillHint.style.display = 'none';

    // Hide after delay
    this.scheduleHide(1000);

    // Reset color after hide
    setTimeout(() => {
      this.comboCounter.style.color = '#ffcc00';
    }, 1100);
  }

  private show(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    this.container.style.opacity = '1';
    this.isVisible = true;
  }

  private scheduleHide(delay: number): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    this.hideTimeout = setTimeout(() => {
      this.container.style.opacity = '0';
      this.isVisible = false;
    }, delay);
  }

  public hide(): void {
    this.container.style.opacity = '0';
    this.isVisible = false;
  }

  public update(state: ComboState, availableCombos: ComboChain[]): void {
    if (!state.activeChain) {
      // Show available combo hints when not in a combo
      if (this.isVisible) {
        this.scheduleHide(500);
      }
      return;
    }

    // Update time remaining bar
    const currentStep = state.activeChain.steps[state.currentStep];
    if (currentStep) {
      const elapsed = Date.now() - state.lastSkillTime;
      const remaining = currentStep.timeWindow - elapsed;
      const percent = Math.max(0, (remaining / currentStep.timeWindow) * 100);
      this.progressBar.style.width = `${percent}%`;
    }
  }

  public destroy(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    this.container.remove();
  }
}
